export default class PrimusService {
  constructor ($log, $rootScope, LocalStorageService) {
    'ngInject'

    this._$rootScope = $rootScope
    this._$log = $log
    this._primus = null
    this._primusLoaded = false
    this._pendingCallbacks = []
    this._connectionStatus = 'disconnected' // 'connected', 'disconnected', 'connecting', 'mock'
    this._statusListeners = []

    // Load primus.js at runtime, because the server serves it.
    this._loadPrimus().then(() => {
      this._initializePrimus($log, LocalStorageService)
    }).catch(err => {
      $log.error('[Primus] Could not load Primus:', err)
      this._setupMockPrimus($log)
      this._initializePrimus($log, LocalStorageService)
    })
  }

  // Load the primus.js script one time.
  _loadPrimus () {
    return new Promise((resolve, reject) => {
      // Primus is already loaded, so return at once.
      if (typeof window !== 'undefined' && typeof window.Primus !== 'undefined') {
        this._primusLoaded = true
        return resolve()
      }

      // Add a script element that loads primus.js.
      const script = document.createElement('script')
      script.src = '/primus/primus.js'
      script.async = true

      script.onload = () => {
        this._primusLoaded = true
        resolve()
      }

      script.onerror = (err) => {
        reject(new Error('Could not load the Primus script: ' + err))
      }

      document.head.appendChild(script)
    })
  }

  // Connect Primus after the script has loaded.
  _initializePrimus ($log, LocalStorageService) {
    // Check if Primus is available
    if (typeof window !== 'undefined' && typeof window.Primus === 'undefined') {
      $log.error('[Primus] Primus global not found, using mock implementation')
      this._setupMockPrimus($log)
    }

    // Read session id from local storage, if any
    const mirage = LocalStorageService.get('mirage')

    // Connect Primus with sessions
    this._primus = new Primus({
      reconnect: {
        max: 60000,
        retries: Infinity
      },
      mirage
    })

    // Save session id to local storage
    this._primus.on('mirage', mirage => {
      LocalStorageService.set('mirage', mirage)
    })

    this._primus.on('open', () => {
      $log.log('[Primus] Connected')
      this._setConnectionStatus('connected')
    })

    this._primus.on('reconnect', opts => {
      $log.log('[Primus] Reconnection attempt %d started', opts.attempt)
      this._setConnectionStatus('connecting')
    })

    this._primus.on('reconnect scheduled', opts => {
      $log.log('[Primus] Reconnection scheduled in %d ms', opts.scheduled)
      this._setConnectionStatus('connecting')
    })

    this._primus.on('reconnect failed', err => {
      $log.warn('[Primus] Reconnect failed: %s', err.message)
      this._setConnectionStatus('disconnected')
    })

    this._primus.on('reconnect timeout', err => {
      $log.warn('[Primus] Reconnect timeout expired: %s', err.message)
      this._setConnectionStatus('disconnected')
    })

    this._primus.on('reconnected', opts => {
      $log.log('[Primus] Reconnected after %d ms', opts.duration)
      this._setConnectionStatus('connected')
    })

    this._primus.on('error', err => {
      $log.error('[Primus] Error ', err)
      this._setConnectionStatus('disconnected')
    })

    this._primus.on('end', () => {
      $log.warn('[Primus] End')
      this._setConnectionStatus('disconnected')
    })

    // Debugging help
    const isDevelopment = typeof process !== 'undefined' &&
      process.env &&
      process.env.NODE_ENV === 'development'

    if (isDevelopment) {
      window.PrimusService = {
        primus: this._primus
      }
    }

    // Run the callbacks that waited for Primus.
    this._pendingCallbacks.forEach(callback => callback())
    this._pendingCallbacks = []
  }

  /**
   * Add a listener that runs in the Angular digest. The returned function
   * removes the listener. It must remove the same function that it attached,
   * and it also works before the Primus script has loaded.
   */
  on (event, listener) {
    const wrappedListener = (...args) => {
      this._$rootScope.$evalAsync(() => listener(...args))
    }

    if (!this._primus) {
      const cancel = this._addPendingCallback(() => this._primus.on(event, wrappedListener))
      return () => {
        cancel()
        if (this._primus) {
          this._primus.removeListener(event, wrappedListener)
        }
      }
    }

    this._primus.on(event, wrappedListener)
    return () => this._primus.removeListener(event, wrappedListener)
  }

  write (data) {
    // Primus is not loaded yet, so keep the write until it is.
    if (!this._primus) {
      return this._addPendingCallback(() => this.write(data))
    }

    this._primus.write(data)
  }

  // Keep a callback until Primus has loaded.
  _addPendingCallback (callback) {
    this._pendingCallbacks.push(callback)
    // Return a function that removes the callback.
    return () => {
      const index = this._pendingCallbacks.indexOf(callback)
      if (index !== -1) {
        this._pendingCallbacks.splice(index, 1)
      }
    }
  }

  // Add a mock Primus when the real script does not load.
  _setupMockPrimus ($log) {
    $log.warn('Initializing mock Primus')
    this._setConnectionStatus('mock')

    // The constructor of the mock Primus.
    window.Primus = function (options) {
      this.options = options || {}
      this.events = {}

      // Act as if the connection opens.
      setTimeout(() => {
        this._emit('open')
        if (options.mirage) {
          this._emit('mirage', options.mirage)
        }
      }, 500)
    }

    // The methods that the app uses.
    window.Primus.prototype = {
      on: function (event, listener) {
        if (!this.events[event]) {
          this.events[event] = []
        }
        this.events[event].push(listener)
        return this
      },

      removeListener: function (event, listener) {
        if (this.events[event]) {
          const index = this.events[event].indexOf(listener)
          if (index !== -1) {
            this.events[event].splice(index, 1)
          }
        }
        return this
      },

      write: function (data) {
        $log.info('[Mock Primus] write', data)
        return this
      },

      _emit: function (event) {
        const listeners = this.events[event] || []
        const args = Array.prototype.slice.call(arguments, 1)

        listeners.forEach(listener => {
          try {
            listener.apply(this, args)
          } catch (e) {
            $log.error('Error i mock Primus event handler', e)
          }
        })
      }
    }
  }

  // Connection status management
  _setConnectionStatus (status) {
    if (this._connectionStatus !== status) {
      this._connectionStatus = status
      this._notifyStatusListeners()
    }
  }

  _notifyStatusListeners () {
    this._statusListeners.forEach(listener => {
      this._$rootScope.$evalAsync(() => {
        listener(this._connectionStatus)
      })
    })
  }

  getConnectionStatus () {
    return this._connectionStatus
  }

  onConnectionStatusChange (listener) {
    this._statusListeners.push(listener)
    // Immediately call with current status
    this._$rootScope.$evalAsync(() => {
      listener(this._connectionStatus)
    })

    // Return unsubscribe function
    return () => {
      const index = this._statusListeners.indexOf(listener)
      if (index !== -1) {
        this._statusListeners.splice(index, 1)
      }
    }
  }
}
